import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { QRButton } from '@base/src/app/models/qr-button.model';
import { Movement } from '../../../models/movement.model';
import { MovementsService } from '../../../services/movements.service';
import { SnackbarService } from '@base/src/app/services/snackbar.service';

export interface MovementDelete {
  anulacion: boolean;
  familyId: number;
  subFamilyId: number;
  concept: string;
  description: string;
  currency: string;
  paymentType: number;
  voucherType: number;
  invoiceNumber?: string | null;
  subTotal: number;
  total: number;
  // Fechas
  expenseDate?: string;
  exchangeRate?: number;
  depositDate?: string;
  incomeDate?: string;
  // Impuestos y extras opcionales
  iva?: number;
  perceivedIIBB?: number;
  others?: number;
  anulacionId: string;
  remoteHost?: string;
  userId: string;
  agencyId: number;
}

@Component({
  selector: 'app-dialog-delete-movement',
  templateUrl: './dialog-delete-movement.component.html',
  styleUrls: ['./dialog-delete-movement.component.scss'],
})
export class DialogDeleteMovementComponent implements OnInit {
  @Input() movementSelected!: Movement;
  @Input() userId: string | undefined;
  @Output() close: EventEmitter<boolean> = new EventEmitter<boolean>();

  movementDelete!: MovementDelete;

  buttonCloseAnnul: QRButton = {
    hierarchy: QRButton.HIERARCHY_3,
    size: QRButton.SIZE_MEDIUM,
    viewValue: 'Volver',
  };

  buttonConfirmAnnul: QRButton = {
    hierarchy: QRButton.HIERARCHY_1,
    size: QRButton.SIZE_MEDIUM,
    viewValue: 'Si, eliminar',
    loader: QRButton.LOADER_SHIMMER,
  };

  isExpense: boolean = false;

  isDeletingMovement = false;

  constructor(
    private readonly movementsService: MovementsService,
    public readonly snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.isExpense = this.movementSelected.movementType === 'Egreso';
    if (this.isDeletingMovement) {
      this.snackbarService.openSnackBar(
        'Eliminando movimiento... por favor espere',
        ''
      );
      return;
    }
    this.isDeletingMovement = true;

    if (this.isExpense) {
      const URL_EXPENSE: string = `expense/${this.movementSelected.id}`;
      this.movementsService
        .get(URL_EXPENSE)
        .then((response) => {
          this.movementDelete = {
            anulacion: response.anulacion,
            familyId: response.familyId,
            subFamilyId: response.subFamilyId,
            concept: 'Anulación ' + response.concept,
            description: response.description,
            currency: response.currency,
            paymentType: response.paymentType,
            voucherType: response.voucherType,
            invoiceNumber: this.movementSelected.invoiceNumber,
            subTotal: -response.subTotal,
            total: -response.total,
            expenseDate: response.expenseDate,
            exchangeRate: response.exchangeRate,
            anulacionId: this.movementSelected.id.toString(),
            remoteHost: '127.0.0.1',
            userId: this.userId ?? '',
            agencyId: response.agencyId,
          };

          if (response.iva) {
            this.movementDelete.iva = -response.iva;
          }

          if (response.perceivedIIBB) {
            this.movementDelete.perceivedIIBB = -response.perceivedIIBB;
          }

          if (response.others) {
            this.movementDelete.others = -response.others;
          }

          if (response.depositDate) {
            this.movementDelete.depositDate = response.depositDate;
          }

          this.isDeletingMovement = false;
        })
        .catch(() => {
          this.snackbarService.openSnackBar(
            '⛔ Error al cargar el movimiento.',
            ''
          );
          this.close.emit(false);
          this.isDeletingMovement = false;
        });
    } else {
      const URL_INCOME: string = `income/${this.movementSelected.id}`;
      this.movementsService
        .get(URL_INCOME)
        .then((response) => {
          this.movementDelete = {
            anulacion: response.anulacion,
            familyId: response.familyId,
            subFamilyId: response.subFamilyId,
            concept: 'Anulación ' + response.concept,
            description: response.description,
            currency: response.currency,
            paymentType: response.paymentType,
            voucherType: response.voucherType,
            subTotal: -response.subTotal,
            total: -response.total,
            incomeDate: response.incomeDate,
            exchangeRate: response.exchangeRate,
            iva: response.iva,
            anulacionId: this.movementSelected.id.toString(),
            remoteHost: '127.0.0.1',
            userId: this.userId ?? '',
            agencyId: response.agencyId,
          };

          if (response.iva) {
            this.movementDelete.iva = -response.iva;
          }
          this.isDeletingMovement = false;
        })
        .catch(() => {
          this.snackbarService.openSnackBar(
            '⛔ Error al cargar el movimiento.',
            ''
          );
          this.isDeletingMovement = false;

          this.close.emit(false);
        });
    }
  }

  async confirmAnnul(): Promise<void> {
    if (this.isDeletingMovement || !this.movementDelete) {
      return;
    }

    this.isDeletingMovement = true;
    const URL_ANNUL = this.isExpense ? 'expense/' : 'income/';

    try {
      const RESPONSE = await this.movementsService.post(
        this.movementDelete,
        URL_ANNUL
      );
      if (RESPONSE?.body?.success) {
        this.snackbarService.openSnackBar(
          `✅ ${RESPONSE.body.responseMsg} `,
          ''
        );
        this.close.emit(true);
      } else {
        this.snackbarService.openSnackBar(
          `⛔ ${RESPONSE?.body?.responseMsg ?? 'Error al anular movimiento'}`,
          ''
        );
        this.close.emit(false);
      }
    } catch (error) {
      this.snackbarService.openSnackBar(
        '⛔ Error de red al anular movimiento. Intente nuevamente.',
        ''
      );
      this.close.emit(false);
    } finally {
      this.isDeletingMovement = false;
    }
  }
}
